import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicButtonComponent } from '@mzic/mzic-arpeggio-ui';
import { MzicSvgComponent, MzicToastService } from '@mzic/mzic-components';
import { LanguageService } from '@mzic/mzic-services';
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';

@Component({
  selector: 'mzic-contact',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslocoModule,
    HeaderComponent,
    FooterComponent,
    MzicButtonComponent,
    MzicSvgComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
  providers: [
    MzicToastService,
    provideTranslocoScope({
      scope: 'contact',
      alias: 'lang',
    }),
  ],
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private toastService = inject(MzicToastService);

  // Section fade animation properties
  private sectionObserver: IntersectionObserver | null = null;
  private fadeableSections: string[] = [
    'hero-section',
    'contacts-section',
    'sell-with-us-section',
    'sell-with-us-mobile',
    'partners-section',
    'partners-mobile',
  ];

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();

      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
  }

  ngAfterViewInit() {
    this.initializeSectionFadeAnimation();
  }

  ngOnDestroy() {
    // Clean up section observer
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  openWhatsApp() {
    const url =
      'https://wa.me/5511945311666/?text=Ol%C3%A1%21%20Estou%20acessando%20atrav%C3%A9s%20do%20site%20de%20investidores%20e%20preciso%20de%20ajuda';
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openEmail() {
    window.location.href = 'mailto:support@mzic.io';
  }

  openAdmEmail() {
    window.location.href = 'mailto:adm@mzic.io';
  }

  openPartnersEmail() {
    window.location.href = 'mailto:adm@mzic.io';
  }

  copyToClipboard(text: string) {
    // Apenas no mobile (máx 768px)
    if (window.innerWidth <= 768) {
      navigator.clipboard.writeText(text).then(
        () => {
          this.toastService.create({
            title: 'Copiado com sucesso',
            icon: 'assets/icons/mzic-check-mark-filled.svg',
            iconColor: 'neutral-700',
            width: '300px',
            customClass: 'success-2',
          });
        },
        (err) => {
          console.error('Erro ao copiar texto: ', err);
        },
      );
    }
  }

  private initializeSectionFadeAnimation() {
    // Mathematical calculation for optimal fade thresholds
    // Desktop: More precise control, smoother transitions
    // Mobile: Faster transitions, less resource intensive
    const isMobile = window.innerWidth <= 768;
    const viewportHeight = window.innerHeight;

    // Calculate intersection thresholds mathematically
    // Desktop: More granular steps for smoother animation
    // Mobile: Fewer steps for better performance
    const thresholdSteps = isMobile ? 5 : 10;
    const thresholds = Array.from(
      { length: thresholdSteps },
      (_, i) => i / (thresholdSteps - 1),
    );

    // Mathematical root margin calculation
    // Creates a buffer zone based on viewport height
    // Desktop: Larger buffer for smoother experience
    // Mobile: Smaller buffer to avoid early triggering
    const rootMarginPercent = isMobile ? 6.75 : 4; // Percentage of viewport height
    const rootMarginPx = Math.round(viewportHeight * (rootMarginPercent / 100));

    // Create observer with calculated mathematical parameters
    this.sectionObserver = new IntersectionObserver(
      (entries) => this.handleSectionIntersection(entries),
      {
        root: null, // Use viewport as root
        rootMargin: `-${rootMarginPx}px 0px -${rootMarginPx}px 0px`, // Symmetric margins
        threshold: thresholds, // Granular thresholds for smooth animation
      },
    );

    // Initialize sections with fade-out state
    // Add observer to each fadeable section
    setTimeout(() => {
      this.fadeableSections.forEach((sectionClass) => {
        const section = document.querySelector(
          `.${sectionClass}`,
        ) as HTMLElement;
        if (section) {
          // Initialize with fade-out state
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');

          // Observe the section
          this.sectionObserver?.observe(section);
        }
      });
    }, 100); // Small delay to ensure DOM is ready
  }

  private handleSectionIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const section = entry.target as HTMLElement;

      // Mathematical fade calculation based on intersection ratio
      const intersectionRatio = entry.intersectionRatio;

      // Calculate fade threshold mathematically
      const isMobile = window.innerWidth <= 768;
      const fadeInThreshold = isMobile ? 0.25 : 0.3;
      const fadeOutThreshold = isMobile ? 0.15 : 0.2;

      // Apply fade based on intersection ratio
      if (intersectionRatio >= fadeInThreshold) {
        section.classList.add('section-fade-in');
        section.classList.remove('section-fade-out');
      } else if (intersectionRatio <= fadeOutThreshold) {
        section.classList.add('section-fade-out');
        section.classList.remove('section-fade-in');
      }

      // Set CSS custom property for advanced animations
      section.style.setProperty(
        '--intersection-ratio',
        intersectionRatio.toString(),
      );
    });
  }
}
